import { motion } from "framer-motion";

/**
 * CategoryBars — barras horizontais finas com %, valor e bolinha colorida.
 * Ordena por value desc automaticamente.
 *
 * Props:
 *   categories: [{ name, value, total?, color }]
 *     - color: classe Tailwind ("bg-category-food") OU hex "#D85A30"
 *   formatValue?: (n) => string  — default formata como BRL
 *   maxItems?: number             — default 6
 */
const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function CategoryBars({
  categories = [],
  formatValue = fmtBRL,
  maxItems = 6,
}) {
  if (!categories.length) {
    return <p className="text-[11px] text-slate-500 text-center py-3">Sem categorias</p>;
  }

  const sorted = [...categories].sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, maxItems);
  const computedMax = sorted[0]?.total ?? sorted[0]?.value ?? 1;

  return (
    <div className="space-y-2.5" data-testid="ds-category-bars">
      {sorted.map((cat, idx) => {
        const pct = Math.max(2, Math.round(((cat.value || 0) / computedMax) * 100));
        const isHex = typeof cat.color === "string" && cat.color.startsWith("#");
        const dotStyle = isHex ? { backgroundColor: cat.color } : {};
        const dotClass = isHex ? "" : (cat.color || "bg-slate-500");
        const barStyle = isHex ? { backgroundColor: cat.color } : {};
        const barClass = isHex ? "" : (cat.color || "bg-slate-500");

        return (
          <div key={cat.name + idx} className="flex items-center gap-2.5">
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`}
              style={dotStyle}
              aria-hidden
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span className="text-[12px] text-slate-300 capitalize truncate">
                  {cat.name}
                </span>
                <span className="text-[11px] tabular-nums text-slate-500">
                  {formatValue(cat.value)}
                </span>
              </div>
              <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: idx * 0.05 }}
                  className={`h-full ${barClass}`}
                  style={barStyle}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
